import * as React from 'react'

import {
  Block,
  getBlockUpdateOverride,
  getValueBasedOnSelectedVariants,
  type NumberBlock as TNumberBlock,
} from '@basehub/data/replicache/index'

import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { mergeRefs } from '~/lib/merge-refs'
import { Input, InputProps } from '~/sotano-kit/primitives/input'
import { useSyncedValue } from '~/hooks/use-synced-value'
import { getPlaceholder } from '../utils/get-placeholder'
import { useBlockInputBaseProps } from '../block-wrapper'
import { useSelectedVariants } from '~/hooks/use-selected-variants'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { useTabsStore } from '../../tabs/store'
import { useEditorMode } from '../../editor-mode-provider'

export const NumberBlockInput = ({
  block,
  path,
  parent,
  readOnly,
  ...props
}: {
  block: TNumberBlock
  parent: Block
  path: string
} & InputProps) => {
  const { data } = useRepoReplicache()
  const editorMode = useEditorMode()
  const isReadOnly = readOnly || editorMode === 'read'
  const replicache = data?.replicache
  const inputRef = React.useRef<HTMLInputElement>(null)
  const baseProps = useBlockInputBaseProps(block)
  const selectedDashboardVariants = useSelectedVariants({
    parentBlock: parent,
    block,
  })

  const _value = React.useMemo(
    () =>
      (
        getValueBasedOnSelectedVariants({
          block,
          variantsToFilterBy: selectedDashboardVariants,
        }) ?? ''
      ).toString(),
    [block, selectedDashboardVariants]
  )

  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)

  const { onChange, ref, value } = useSyncedValue<HTMLInputElement>({
    value: _value,
    customEventsKey: `synced-value:number:${idToSourceBlockId(block)}`,
    setValue: React.useCallback(
      async (v, { internalValueRef, customEventsKey }) => {
        if (!replicache?.mutate) return
        const getParams = (v: string): UpdateBlockParams => ({
          id: block.id,
          instanceChildTarget: block.instanceChildTarget,
          data: getBlockUpdateOverride({
            block,
            newValue: v === '' ? null : Number(v),
            selectedVariants: selectedDashboardVariants,
          }),
          path,
          strategy: 'merge',
          isoNow: new Date().toISOString(),
        })
        const { undo } = await replicache.mutate.updateBlock(getParams(v))
        if (undo) {
          undoManager.current?.push({
            redo: [
              {
                op: 'focus',
                args: {
                  preferredTabKey: activeTabKey,
                  focus: {
                    type: 'block:value',
                    blockId: idToSourceBlockId({
                      id: block.id,
                      sourceBlockId: block.sourceBlockId,
                    }),
                  },
                },
              },
              { op: 'updateBlock', args: getParams(internalValueRef.current) },
              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
            undo: [
              {
                op: 'focus',
                args: {
                  preferredTabKey: activeTabKey,
                  focus: {
                    type: 'block:value',
                    blockId: idToSourceBlockId({
                      id: block.id,
                      sourceBlockId: block.sourceBlockId,
                    }),
                  },
                },
              },
              { op: 'blockBatchMutations', args: { mutations: undo } },
              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
          })
        }
      },
      [
        replicache?.mutate,
        block,
        selectedDashboardVariants,
        path,
        undoManager,
        activeTabKey,
      ]
    ),
    rateLimitMs: 1000,
  })

  return (
    <Input
      {...baseProps}
      placeholder={getPlaceholder(block.title)}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-number-block
      ref={mergeRefs([inputRef, ref])}
      readOnly={isReadOnly}
      {...props}
    />
  )
}
